import { noop } from 'lodash';
import { type ReactChild, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import { GripVerticalIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { cn } from '@air/utils-tailwind';

type DragItemTypeDefault = {
  type: string;
  index: number;
  [key: string]: unknown;
};

export type ReactDndDragWrapperProps<T> = {
  children: ReactChild;
  dragItem: T;
  isDraggable?: boolean;
  swapItems: (dragIndex: number, hoverIndex: number) => void;
  handleDrop?: (dragItem: T) => void;
  className?: string;
};

const ReactDndDragWrapper = <T extends DragItemTypeDefault>({
  children,
  dragItem,
  swapItems,
  isDraggable = true,
  handleDrop = noop,
  className,
}: ReactDndDragWrapperProps<T>) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [{ isDragging }, drag] = useDrag({
    type: dragItem.type,
    item: () => dragItem,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const [, drop] = useDrop({
    accept: dragItem.type,
    drop(item: T) {
      handleDrop(item);
    },
    hover(item: T, monitor) {
      const dragIndex = item.index;
      const hoverIndex = dragItem.index;

      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      if (!hoverBoundingRect) {
        return;
      }
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = (clientOffset && clientOffset.y - hoverBoundingRect.top) || 0;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      swapItems(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });
  drag(drop(ref));
  const opacity = isDragging ? 0 : 1;

  return (
    <div ref={ref} draggable={isDraggable} className={cn('flex items-center', className)} style={{ opacity }}>
      <IconButton
        adsIcon
        icon={GripVerticalIcon}
        disabled={!isDraggable}
        size="small"
        appearance="ghost"
        color="gray"
        label="Move"
      />
      <div className="grow">{children}</div>
    </div>
  );
};

export { ReactDndDragWrapper };
